<template>
    <b-button-toolbar aria-label="Toolbar with button groups and dropdown menu">
        <b-button
            size="sm"
            v-if="isMobile"
            :disabled="!hasFeatureSelection"
            @click="$_onSaveFeatures()"
        >
            <img
                src="~@/assets/imported-icons/ic_fluent_save_20_regular.svg"
                :alt="$t('saveSelection')"
                :title="buttonTitle(!hasFeatureSelection, $t('saveSelection'))"
            />
        </b-button>
        <b-button
            size="sm"
            v-if="isMobile"
            :disabled="!(hasFeatureSelection && hasActiveWfsLayers)"
            @click="$_onBufferSelectFromSelectedFeatures()"
        >
            <img
                src="~@/assets/imported-icons/ic_fluent_resize_large_20_regular.svg"
                :alt="$t('makeBufferSelection')"
                :title="
                    buttonTitle(!hasFeatureSelection, $t('makeBufferSelection'))
                "
            />
        </b-button>
        <!-- Chart button -->
        <b-button
            size="sm"
            v-if="isMobile"
            @click="$_onShowChart()"
            :disabled="!hasFeatureSelection"
        >
            <img
                src="~@/assets/imported-icons/ic_fluent_data_bar_vertical_20_regular.svg"
                :alt="$t('showAsChart')"
                :title="$t('showAsChart')"
            />
        </b-button>
        <template v-if="isExportable">
            <b-button
                size="sm"
                :disabled="!hasExportSelection"
                @click="$_exportToExcel()"
            >
                <img
                    src="~@/assets/icons/ng_fluent_downloadexcel_20_regular.svg"
                    :alt="$t('exportExcel')"
                    :title="buttonTitle(!hasExportSelection, $t('exportExcel'))"
                />
            </b-button>
            <b-dropdown
                size="sm"
                toggle-class="custom-button"
                :disabled="!hasExportSelection"
            >
                <template #button-content>
                    <img
                        src="~@/assets/imported-icons/ic_fluent_arrow_download_20_regular.svg"
                        :alt="$t('downloadExportFile')"
                        :title="
                            buttonTitle(
                                !hasExportSelection,
                                $t('downloadExportFile')
                            )
                        "
                    />
                </template>
                <b-dropdown-item @click="$_exportToCSV()">
                    {{ $t('downloadCSV') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$_exportToGeoJSON()">
                    {{ $t('downloadGeoJSON') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$_exportToGML()">
                    {{ $t('downloadGML') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$_exportToShapeFile()">
                    {{ $t('downloadShape') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$_exportToKML()">
                    {{ $t('downloadKML') }}
                </b-dropdown-item>
                <b-dropdown-item @click="$_exportToDXF()">
                    {{ $t('downloadDXF') }}
                </b-dropdown-item>
            </b-dropdown>
        </template>
        <!-- Button for change of multiple feedbacks -->
        <b-button
            size="sm"
            v-if="showEdit"
            :disabled="!hasFeatureSelection"
            @click="$_onEditFeatures()"
        >
            <img
                src="~@/assets/imported-icons/ic_fluent_edit_20_regular.svg"
                :alt="$t('editFeedbacks')"
                :title="buttonTitle(!hasFeatureSelection, $t('editFeedbacks'))"
            />
        </b-button>
        <b-button
            v-if="group.remove"
            size="sm"
            :disabled="!hasFeatureSelection"
            @click="$_onDeleteSelectedFeatures()"
        >
            <img
                src="~@/assets/imported-icons/ic_fluent_delete_20_regular.svg"
                :alt="$t('deleteSelected')"
                :title="buttonTitle(!hasFeatureSelection, $t('deleteSelected'))"
            />
        </b-button>
        <!-- Toolbar button for relations -->
        <relations-toolbar-button
            v-if="hasRelations"
            :exportData="exportData"
            :featureData="featureData"
            :relations="relations"
            :layer="group.layer"
        />
        <b-button size="sm ml-auto" @click="$_onShowFilter()">
            <img
                v-if="showFilter"
                src="~@/assets/icons/ng_fluent_filteroff_20_regular.svg"
                :alt="$t('labelFiltersTurnOff')"
                :title="$t('labelFiltersTurnOff')"
            />
            <img
                v-else
                src="~@/assets/icons/ng_fluent_filteron_20_regular.svg"
                :alt="$t('labelFilters')"
                :title="$t('labelFilters')"
            />
        </b-button>
    </b-button-toolbar>
</template>

<script>
    import { mapGetters } from 'vuex';
    import GeoJSON from 'ol/format/GeoJSON';
    import { LineString, MultiLineString } from 'ol/geom';
    import KML from 'ol/format/KML';
    import GML from 'ol/format/GML3';

    import {
        exportToExcel,
        exportToCSV,
        exportToDxf
    } from '../../../../services/utilities/export';
    import {
        CONTENT_TYPE_ID,
        INTERFACE_PANEL_TRIGGERS,
        SELECTION_MODES,
        PROJECTION_CODE,
        NEDGLOBE_MAP_PROJECTION
    } from '../../../../constants';
    import RelationsToolbarButton from '../toolbars/RelationsToolbarButton.vue';

    export default {
        components: { 'relations-toolbar-button': RelationsToolbarButton },
        props: {
            exportData: { type: Array },
            featureData: { type: Array },
            group: { type: Object, required: true }
        },
        data() {
            return {
                useAliasForColumnNames: true,
                showFilter: false
            };
        },
        computed: {
            ...mapGetters([
                'getActivatedWFSLayers',
                'isMobile',
                'getLayerRelations',
                'getMainMap'
            ]),
            buttonTitle() {
                return (isDisabled, title) =>
                    isDisabled ? `${title}\n(${this.$t('selectRows')})` : title;
            },
            hasFeatureSelection() {
                return this.hasExportSelection;
            },
            hasActiveWfsLayers: function () {
                return this.getActivatedWFSLayers?.length > 0 || false;
            },
            hasExportSelection: function () {
                return this.exportData?.length > 0 || false;
            },
            hasDeleteSelection: function () {
                return this.featureData?.length > 0 || false;
            },
            isExportable: function () {
                return this.group.exportable;
            },
            exportFields: function () {
                return this.exportData.map((item) => {
                    return Object.keys(item).reduce((acc, field) => {
                        if (item[field].exportable) {
                            const columnName =
                                item[field].alias?.length > 0 &&
                                this.useAliasForColumnNames
                                    ? item[field].alias
                                    : field;
                            acc[columnName] = item[field].displayValue ?? '';
                        }
                        return acc;
                    }, {});
                });
            },
            exportFeatureData: function () {
                return this.featureData.map((feature, index) => {
                    const clonedFeature = feature.clone();
                    const currentFields = Object.keys(
                        clonedFeature.getProperties()
                    ).filter((prop) => prop !== 'geometry');
                    currentFields.forEach((prop) => {
                        clonedFeature.unset(prop);
                    });
                    clonedFeature.setProperties(this.exportFields[index]);
                    return clonedFeature;
                });
            },
            downloadElementId() {
                return `exportIcon_${this.group.order}`;
            },
            relations() {
                return this.getLayerRelations(this.group.layer.id);
            },
            hasRelations() {
                return this.relations?.length > 0;
            },
            showEdit() {
                return (
                    this.isMobile &&
                    this.group.editable &&
                    this.group.layer.extended.contentTypeId ===
                        CONTENT_TYPE_ID.FEEDBACK
                );
            }
        },
        methods: {
            $_exportToCSV() {
                this.useAliasForColumnNames = true;
                const dataToExport = this.exportFields;
                const fieldSeparator = dataToExport.fieldSeparator
                    ? dataToExport.fieldSeparator
                    : ';';
                const fileName = this.group.title
                    ? `${this.group.title}.csv`
                    : 'export.csv';
                exportToCSV(fileName, fieldSeparator, dataToExport);
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'csv'
                });
            },
            async $_exportToExcel() {
                this.useAliasForColumnNames = true;
                const dataToExport = this.exportFields;
                const fileName = this.group.title
                    ? `${this.group.title}.xlsx`
                    : 'export.xlsx';
                await exportToExcel(fileName, dataToExport);
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'excel'
                });
            },

            $_getGeoJSON() {
                const geoJsonObject = new GeoJSON({
                    dataProjection: PROJECTION_CODE,
                    featureProjection: PROJECTION_CODE
                });
                const geoJSONString = geoJsonObject.writeFeatures(
                    this.exportFeatureData
                );
                return JSON.parse(geoJSONString);
            },

            $_exportToGeoJSON() {
                this.useAliasForColumnNames = true;
                const fileName = this.group.title
                    ? `${this.group.title}.JSON`
                    : 'export.JSON';
                const geoJsonParsed = this.$_getGeoJSON();
                // current implementation possibly not according to GEOJson spec,
                // decided to implement for convenience of dutch customers
                // supported by qgis
                geoJsonParsed.crs = {
                    type: 'name',
                    properties: { name: 'urn:ogc:def:crs:EPSG::28992' }
                };
                const data = `data:text/plain;charset=utf-8,${encodeURIComponent(
                    JSON.stringify(geoJsonParsed)
                )}`;
                const link = document.createElement('a');
                link.setAttribute('href', data);
                link.setAttribute('download', fileName);
                link.click();
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'geojson'
                });
            },

            $_exportToDXF() {
                this.useAliasForColumnNames = false;
                const title = this.group.title ?? 'export';
                const fileName = `${title}.dxf`;
                const layerName = 'export';
                const geoJson = this.$_getGeoJSON();
                const dxfString = exportToDxf(geoJson, layerName);

                const data = `data:text/plain;charset=utf-8,${dxfString}`;
                const link = document.createElement('a');
                link.setAttribute('href', data);
                link.setAttribute('download', fileName);
                link.click();
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'dxf'
                });
            },

            $_exportToGML() {
                this.useAliasForColumnNames = false;
                const fileName = this.group.title
                    ? `${this.group.title}.gml`
                    : 'export.gml';
                const GMLObject = new GML({
                    featureNS: 'http://www.opengis.net/gml',
                    srsName: PROJECTION_CODE,
                    featureType: ['Polygon']
                });
                const GMLString = GMLObject.writeFeatures(this.exportFeatureData, {
                    dataProjection: NEDGLOBE_MAP_PROJECTION,
                    featureProjection: NEDGLOBE_MAP_PROJECTION
                });
                // eslint-disable-next-line max-len
                const data = `data:application/gml+xml;charset=utf-8,<?xml version="1.0" encoding="utf-8"?><FeatureCollection>${encodeURIComponent(
                    GMLString
                )}</FeatureCollection>`;
                const link = document.createElement('a');
                link.setAttribute('href', data);
                link.setAttribute('download', fileName);
                link.click();
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'gml'
                });
            },

            async $_exportToShapeFile() {
                this.useAliasForColumnNames = true;
                const file = this.group.title || 'shape';
                const geoJsonObject = new GeoJSON({
                    dataProjection: NEDGLOBE_MAP_PROJECTION,
                    featureProjection: NEDGLOBE_MAP_PROJECTION
                });
                const updatedFeatureData = this.exportFeatureData.map((feature) => {
                    const itemGeometry = feature.getGeometry();
                    if (itemGeometry instanceof LineString) {
                        const newGeometry = new MultiLineString([
                            itemGeometry.getCoordinates()
                        ]);
                        feature.setGeometry(newGeometry);
                    }
                    return feature;
                });

                const geoJSON =
                    geoJsonObject.writeFeaturesObject(updatedFeatureData);
                const shpwrite = await import('shp-write-nedglobe');
                // (optional) set names for feature types and zipped folder
                const options = {
                    file,
                    types: {
                        point: 'Point',
                        polygon: 'Polygon',
                        LineString: 'LineString'
                    }
                };
                // a GeoJSON bridge for features
                // triggers a download of a zip file with shapefiles contained within.
                shpwrite.download(geoJSON, options);
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'shape'
                });
            },

            async $_exportToKML() {
                this.useAliasForColumnNames = false;
                const fileName = this.group.title
                    ? `${this.group.title}.kml`
                    : 'export.kml';

                const format = new KML();
                const mapObject = this.getMainMap;
                const map = mapObject.map;
                const view = map?.getView();
                const projection = view?.getProjection();
                // Write features to KML, transforming from RD (EPSG:28992) to WGS 84 (EPSG:4326)
                const kmlData = format.writeFeatures(this.exportFeatureData, {
                    dataProjection: 'EPSG:4326', // Output projection for Google Maps (WGS 84)
                    featureProjection: projection // Input projection (RD)
                });

                const data = `data:text/plain;charset=utf-8,${kmlData}`;
                const link = document.createElement('a');
                link.setAttribute('href', data);
                link.setAttribute('download', fileName);
                link.click();
                this.$store.dispatch('trackUserEvent', {
                    name: 'export',
                    stage: 'kml'
                });
            },

            $_onDeleteSelectedFeatures() {
                const layer = this.group.layer;
                const panelName =
                    layer.extended.contentTypeId === CONTENT_TYPE_ID.FEEDBACK
                        ? INTERFACE_PANEL_TRIGGERS.FEEDBACK_EDIT
                        : INTERFACE_PANEL_TRIGGERS.MEASURE_EDIT;
                this.$store.dispatch('togglePanel', {
                    panelName,
                    visible: true,
                    data: { layer: this.group.layer, features: this.featureData },
                    mode: 'delete'
                });
            },
            $_onBufferSelectFromSelectedFeatures() {
                const panelName = INTERFACE_PANEL_TRIGGERS.SELECTION;
                this.$store.dispatch('togglePanel', {
                    panelName,
                    visible: true,
                    data: { layer: this.group.layer, features: this.featureData },
                    mode: SELECTION_MODES.BUFFER
                });
                this.$store.dispatch('trackUserEvent', { name: 'buffer' });
            },
            $_onSaveFeatures() {
                const features = this.featureData.map((feature) => feature.clone());
                this.$store.commit('SET_USERMAPS_IMPORT_FEATURES', {
                    features,
                    name: `${this.group.title}_v2`
                });
            },
            $_onShowChart() {
                const features = this.featureData.map((feature) => feature.clone());

                this.$store.dispatch('togglePanel', {
                    panelName: INTERFACE_PANEL_TRIGGERS.CHART_DISPLAY,
                    visible: true,
                    data: {
                        features,
                        layerName: this.group.layer.data.title
                    }
                });
            },
            $_onEditFeatures() {
                const layer = this.group.layer;
                if (layer.extended.contentTypeId === CONTENT_TYPE_ID.FEEDBACK) {
                    this.$store.commit('RESET_MAP_FEATURES', { mapName: 'main' });
                    let panelName = '';
                    if (
                        layer.extended.serviceType === 'WFS' &&
                        layer.extended.isTransactional
                    ) {
                        panelName = 'WfsTEdit';
                    } else {
                        panelName = 'feedbackEdit';
                    }
                    if (this.featureData.length === 1) {
                        this.$store.dispatch('togglePanel', {
                            panelName,
                            visible: true,
                            data: { layer, feature: this.featureData[0] }
                        });
                    } else {
                        this.$store.dispatch('togglePanel', {
                            panelName,
                            visible: true,
                            data: { layer, features: this.featureData }
                        });
                    }
                }
            },
            $_onShowFilter() {
                this.showFilter = !this.showFilter;
                this.$emit('showFilter', this.showFilter);
            }
        }
    };
</script>

<style lang="scss">
.btn-group .custom-button {
    border: 1px solid transparent;
    &:disabled {
        color: $gray-04;
        background-color: transparent;
        border: 1px solid transparent;
    }
}
</style>
<style lang="scss" scoped>
.btn {
    border: 1px solid transparent;

    &:disabled {
        color: $gray-04;
        background-color: transparent;
        border: 1px solid transparent;
    }
}

.overview-toolbar {
    display: flex;
    padding: 4px 0px 4px 10px;
}

.overview-toolbar > div > div:last-child {
    width: 20px;
    height: 20px;
}
</style>
